import Flickity from 'flickity';
import EventHandler from './EventHandler';
import Layout from './Layout';
import Video from './Video';
import Drift from 'drift-zoom';
import ProductClickToZoom from './ProductClickToZoom';
import { rimg_shopify } from './rimg';
import jQuery from 'jquery';
import { GetProductData } from '@themes/common/data';
let loadPlyrStyles = null;
export default class ProductGallery {
    constructor({ el, settings, isQuickshop, sectionContext }) {
        this.swiped = false;
        this.el = el;
        this.settings = settings;
        this.isQuickshop = isQuickshop || false;
        this.sectionContext = sectionContext || 'default';
        this.events = new EventHandler();
        this.viewer = this.el.querySelector('[data-gallery-viewer]');
        this.navigation = this.el.querySelector('[data-gallery-navigation]');
        this.navScroller = this.el.querySelector('[data-gallery-scroller]');
        this.navScrollerButtons = this.el.querySelectorAll('[data-gallery-scroll-button]');
        this.figures = this.viewer.querySelectorAll('[data-gallery-figure]');
        this.thumbnails = this.navigation.querySelectorAll('[data-gallery-thumbnail]');
        this.zoomButton = this.viewer.querySelector('[data-gallery-expand]');
        this.selected = {
            figure: this.viewer.querySelector('[data-gallery-selected="true"]'),
            thumbnail: this.navigation.querySelector('[data-gallery-selected="true"]'),
        };
        this.models = {};
        this.videos = {};
        this.selectedVideo = null;
        this.selectedModel = null;
        this.showingInitialSlide = true;
        this._setupSelectedVideo = this._setupSelectedVideo.bind(this);
        const features = [];
        if (this.el.querySelector('[data-media-type="video"]')) {
            if (!(loadPlyrStyles instanceof Promise)) {
                loadPlyrStyles = new Promise((resolve) => {
                    const stylesheet = document.createElement('link');
                    stylesheet.setAttribute('rel', 'stylesheet');
                    stylesheet.href = 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css';
                    stylesheet.onload = resolve;
                    document.body.appendChild(stylesheet);
                });
            }
            features.push({
                name: 'video-ui',
                version: '1.0',
                onLoad: () => loadPlyrStyles.then(() => this._setupSelectedVideo()),
            });
        }
        if (this.el.querySelector('[data-media-type="model"]')) {
            this.viewInYourSpaceEl = this.el.querySelector('[data-shopify-xr]');
            this.events.register(this.viewInYourSpaceEl, 'click', () => this._onViewInYourSpaceClick());
            features.push({
                name: 'model-viewer-ui',
                version: '1.0',
                onLoad: this._onModelLibraryLoad.bind(this),
            });
            features.push({
                name: 'shopify-xr',
                version: '1.0',
            });
        }
        if (features.length) {
            globalThis.Shopify.loadFeatures(features);
        }
        if (this.isQuickshop) {
            rimg_shopify.watch(this.el); // Because of the way the quickshop is animated to open, if a video is first in the
            // gallery the height of the slide will not be correct, so we need to wait until all the
            // transitions end. This will only fire once, as opposed to listening to transitionend.
            if (this.figures[0].dataset.mediaType === 'video') {
                setTimeout(() => this._setSlideHeight(), 800);
            }
        }
        this.events.register(this.navScroller, 'click', (e) => this._onThumbnailClick(e));
        this.events.register(this.navScroller, 'scroll', () => this._handleScrollButtonVisibility());
        if (this.navScrollerButtons[0]) {
            this.events.register(this.navScrollerButtons[0], 'click', () => this._onScrollButtonClick(true));
            this.events.register(this.navScrollerButtons[1], 'click', () => this._onScrollButtonClick(false));
        }
        if (this.settings.gallery_click_to_zoom !== 'disabled') {
            const onZoomButtonClick = (e) => {
                if (!jQuery(e.target).hasClass('flickity-button-icon') && !jQuery(e.target).hasClass('flickity-prev-next-button')) {
                    e.stopPropagation();
                    this._openImageZoom();
                }
            };
            this.events.register(this.zoomButton, 'click', (e) => onZoomButtonClick(e));
            this.events.register(this.viewer, 'click', (e) => onZoomButtonClick(e));
        }
        this._onResize = this._onResize.bind(this);
        this.events.register(window, 'resize', () => this._onResize());
        this._setupFlickity();
        this._handleScrollButtonVisibility();
        this._selectMediaByEl(this.selected.figure);
    }
    selectMediaByVariant(variant) {
        var _a;
        const productData = GetProductData(variant.id);
        const featured_media = (_a = variant.featured_media) !== null && _a !== void 0 ? _a : productData === null || productData === void 0 ? void 0 : productData.featured_media;
        if (!featured_media)
            return;
        const slide = this.viewer.querySelector(`[data-media="${featured_media.id}"]`);
        if (!slide)
            return;
        this._selectMediaByEl(slide);
    }
    unload() {
        this.events.unregisterAll();
        for (let i; i < this.figures.length; i++) {
            if (this.models[i]) {
                this.models[i].destroy();
            }
            if (this.videos[i]) {
                this.videos[i].destroy();
            }
        }
        if (this.isQuickshop) {
            rimg_shopify.unwatch(this.el);
        }
        this._closeImageZoom();
        this._disableHoverZoom();
        this._disableFlickity();
    }
    _onModelLibraryLoad() {
        const onPlayListener = () => {
            var _a, _b;
            if (this.flickity) {
                (_b = (_a = this.flickity).unbindDrag) === null || _b === void 0 ? void 0 : _b.call(_a)();
            }
        };
        const onPauseListener = () => {
            var _a, _b;
            if (this.flickity) {
                (_b = (_a = this.flickity).bindDrag) === null || _b === void 0 ? void 0 : _b.call(_a)();
            }
        };
        this.el.querySelectorAll('.product-gallery--model').forEach((figureEl) => {
            const modelEl = figureEl.querySelector('model-viewer');
            const { galleryIndex } = figureEl.dataset;
            const controls = ['zoom-in', 'zoom-out'];
            if (document.fullscreenEnabled)
                controls.push('fullscreen');
            this.models[galleryIndex] = new Shopify.ModelViewerUI(modelEl, {
                controls,
            });
            this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_play', onPlayListener);
            this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_pause', onPauseListener);
        });
    }
    _onResize() {
        // If the resize was trigger by a fullscreen, do not do anything
        if (document.fullscreenElement) {
            return;
        }
        this._setupFlickity();
        if (Layout.isGreaterThanBreakpoint('L', true) && !this.drift) {
            this._setupHoverZoom();
        }
        else if (Layout.isLessThanBreakpoint('L')) {
            this._disableHoverZoom();
        }
        this._adjustGalleryPositioning();
        this._handleScrollButtonVisibility();
    }
    _onThumbnailClick(e) {
        let button;
        this.showingInitialSlide = false; // The target will be the button itself if the user is using a keyboard to navigate, but
        // otherwise it will be the image that is clicked on.
        if (e.target.classList.contains('product-gallery--media-thumbnail')) {
            button = e.target;
        }
        else if (e.target.parentNode.parentNode.classList.contains('product-gallery--media-thumbnail')) {
            button = e.target.parentNode.parentNode;
        }
        else {
            return;
        }
        const selectedIndex = parseInt(button.dataset.galleryIndex, 10);
        if (this.flickity) {
            this.flickity.select(selectedIndex);
        }
        else {
            this._selectMediaByIndex(selectedIndex);
        }
    }
    _onViewInYourSpaceClick(e) {
        const { currentTarget } = e;
        const figure = this.viewer.querySelector(`[data-media="${currentTarget.dataset.shopifyModel3dId}"]`);
        this._selectMediaByEl(figure);
    }
    _selectMediaByEl(el) {
        this._selectMediaByIndex(parseInt(el.dataset.galleryIndex, 10));
    }
    _selectMediaByIndex(index) {
        var _a, _b, _c, _d;
        const figure = this.figures[index];
        this.selected.figure.dataset.gallerySelected = false;
        this.selected.figure.setAttribute('aria-hidden', true);
        this.selected.figure = figure;
        this.selected.figure.dataset.gallerySelected = true;
        this.selected.figure.setAttribute('aria-hidden', false);
        const figureIndex = parseInt(this.selected.figure.dataset.galleryIndex, 10);
        this.selected.thumbnail.dataset.gallerySelected = false;
        this.selected.thumbnail = this.thumbnails[figureIndex];
        this.selected.thumbnail.dataset.gallerySelected = true;
        const { media, mediaType } = this.selected.figure.dataset;
        this.viewer.dataset.selectedMediaType = mediaType;
        this._disableHoverZoom();
        if (this.selectedModel) {
            this.selectedModel.pause();
        }
        if (this.models[figure.dataset.galleryIndex]) {
            this.selectedModel = this.models[figure.dataset.galleryIndex];
            if (!document.documentElement.classList.contains('has-touch') && !this.showingInitialSlide) {
                this.selectedModel.play();
            }
        }
        if (this.selectedVideo) {
            this.selectedVideo.pause();
        }
        if (this.viewInYourSpaceEl) {
            this.viewInYourSpaceEl.dataset.shopifyModel3dId = this.viewInYourSpaceEl.dataset.defaultModelId;
        }
        switch (mediaType) {
            case 'model':
                this.viewInYourSpaceEl.dataset.shopifyModel3dId = media;
                break;
            case 'video':
            case 'external_video':
                this._setupSelectedVideo();
                break;
            case 'image':
                this._setupHoverZoom();
                break;
            default:
                break;
        }
        if (this.flickity) {
            (_b = (_a = this.flickity).bindDrag) === null || _b === void 0 ? void 0 : _b.call(_a);
            (_d = (_c = this.flickity).select) === null || _d === void 0 ? void 0 : _d.call(_c, index);
        }
        this._adjustGalleryPositioning();
    }
    _setupSelectedVideo() {
        if (this.selectedVideo) {
            this.selectedVideo.pause();
        }
        const { mediaType, galleryIndex } = this.selected.figure.dataset;
        const shouldAutoplay = Layout.isGreaterThanBreakpoint('L', true) && this.settings.gallery_video_autoplay && !this.showingInitialSlide;
        if (mediaType === 'video' || mediaType === 'external_video') {
            let videoElement;
            if (this.videos[galleryIndex]) {
                this.selectedVideo = this.videos[galleryIndex];
                if (shouldAutoplay)
                    this.selectedVideo.play();
            }
            else {
                if (mediaType === 'video') {
                    // Plyr hasn't loaded yet, will be called again after loadFeatures
                    if (!Shopify.Plyr)
                        return;
                    videoElement = this.selected.figure.querySelector('video');
                    this.selectedVideo = new Shopify.Plyr(videoElement, {
                        loop: {
                            active: this.settings.gallery_video_looping,
                        },
                    });
                    if (shouldAutoplay)
                        this.selectedVideo.play();
                }
                else {
                    videoElement = this.selected.figure.querySelector('[data-video]');
                    this.selectedVideo = new Video(videoElement, {
                        loop: this.settings.gallery_video_looping,
                    });
                    if (shouldAutoplay)
                        this.selectedVideo.play();
                }
                this.videos[galleryIndex] = this.selectedVideo;
            }
            this.selected.figure.focus();
        }
    }
    _setupHoverZoom() {
        if (this.settings.gallery_hover_zoom === 'disabled' ||
            !Layout.isGreaterThanBreakpoint('L', true) ||
            this.selected.figure.dataset.mediaType !== 'image' ||
            document.documentElement.classList.contains('has-touch'))
            return; // Only select the image loaded with JS
        const selectedImage = this.selected.figure.querySelector('[data-rimg]:not([data-rimg="noscript"])');
        selectedImage.setAttribute('data-zoom', this.selected.figure.getAttribute('data-zoom'));
        this.drift = new Drift(selectedImage, {
            paneContainer: this.settings.gallery_hover_zoom === 'separate' ? this.el.querySelector('[data-zoomed-image]') : this.selected.figure,
            inlinePane: 0,
            hoverBoundingBox: this.settings.gallery_hover_zoom === 'separate',
            handleTouch: false,
            onShow: () => {
                if (this.settings.gallery_hover_zoom === 'separate') {
                    const productMain = document.querySelector('.product-main');
                    const productAlt = document.querySelector('.product-form--alt');
                    productMain === null || productMain === void 0 ? void 0 : productMain.classList.add('product-gallery--fade');
                    if (productAlt)
                        productAlt.classList.add('product-gallery--fade');
                }
            },
            onHide: () => {
                if (this.settings.gallery_hover_zoom === 'separate') {
                    const productMain = document.querySelector('.product-main');
                    const productAlt = document.querySelector('.product-form--alt');
                    productMain === null || productMain === void 0 ? void 0 : productMain.classList.remove('product-gallery--fade');
                    if (productAlt)
                        productAlt.classList.remove('product-gallery--fade');
                }
            },
        });
    }
    _disableHoverZoom() {
        if (this.drift) {
            this.drift.disable();
            this.drift = null;
        }
    }
    _setupFlickity() {
        if (this.flickity)
            return;
        this.flickity = new Flickity(this.viewer, {
            accessibility: false,
            adaptiveHeight: true,
            cellSelector: '.product-gallery--media',
            contain: true,
            friction: 1,
            freeScroll: false,
            initialIndex: parseInt(this.selected.figure.dataset.galleryIndex, 10),
            lazyLoad: false,
            percentPosition: false,
            prevNextButtons: true,
            pageDots: false,
            selectedAttraction: 1,
            setGallerySize: false,
            wrapAround: true,
        });
        this.flickityEvent = this.events.register(this.viewer, 'rimg:load', () => {
            this._setSlideHeight();
        });
        this.flickity.on('change', (index) => {
            this.showingInitialSlide = false;
            this._selectMediaByEl(this.figures[index]);
        }); // Flickity controls the pointerdown event which prevents model-viewer-ui from
        // triggering .play() on mouseup. This returns this functionality.
        this.flickity.on('staticClick', (event, pointer, cellElement) => {
            const figure = cellElement.querySelector('.product-gallery--model');
            if (figure && this.models[figure.dataset.galleryIndex]) {
                const model = this.models[figure.dataset.galleryIndex];
                model.play();
            }
        });
        this.flickity.on('pointerDown', () => {
            // Removes the focus ring when swiping with touch device
            this.viewer.blur();
        }); // We need to disable the product zoom open if a user is swiping
        // This was an issue on mouse gestures, not on touch
        if (this.settings.gallery_click_to_zoom !== 'disabled' && document.documentElement.classList.contains('no-touch')) {
            this.flickity.on('dragEnd', () => {
                this.swiped = true;
            });
        }
        this._setSlideHeight();
    }
    _disableFlickity() {
        if (this.flickity) {
            this.flickity.destroy();
            this.flickity = null;
            this.events.unregister(this.flickityEvent);
        }
    }
    _setSlideHeight() {
        this.navigation.classList.remove('loading'); // Use the stored image height for situations where the DOM is in a midway state.
        var containerHeight = this.storedImageHeight || this.selected.figure.getBoundingClientRect().height;
        this.storedImageHeight = null;
        if (containerHeight > 50) {
            this.viewer.style.height = `${containerHeight}px`;
        }
    }
    _adjustGalleryPositioning() {
        if (this.settings.gallery_thumbnail_position === 'left') {
            if (this.isQuickshop) {
                if (Layout.isGreaterThanBreakpoint('L', true) && this.sectionContext === 'default') {
                    this.el.dataset.productGalleryThumbnails = 'left';
                }
                else {
                    this.storedImageHeight = this.selected.figure.getBoundingClientRect().height;
                    this.el.dataset.productGalleryThumbnails = 'center';
                }
            } // We need to adjust the width of the thumbnails if they are positioned left
            // and they wrap into 2 columns. CSS flexbox does not cause the width of the
            // element to stretch. Looks like it's a problem with flexbox when wrapping columns:
            // https://stackoverflow.com/questions/33891709/when-flexbox-items-wrap-in-column-mode-container-does-not-grow-its-width
            // We want the thumbnails to always be placed 'below' for the shoppable image, ie: 'default'
            this.navigation.style.width = '';
            this.navigation.style.maxWidth = '';
            this._setSlideHeight();
            if (Layout.isLessThanBreakpoint('S')) {
                this._adjustMobileThumbnailPosition();
            }
        }
        else {
            this._setSlideHeight();
            this._adjustMobileThumbnailPosition();
        }
    }
    _adjustMobileThumbnailPosition() {
        // This brings the thumbnail into view if it is not visible on the screen after
        // a user swipes to a slide that it is tied to. Only important on mobile.
        if (Layout.isLessThanBreakpoint('S')) {
            const thumbBounds = this.selected.thumbnail.getBoundingClientRect();
            const thumbWrapperBounds = this.navScroller.getBoundingClientRect();
            if (this.selected.thumbnail.offsetLeft + thumbBounds.width + 30 - this.navScroller.scrollLeft > thumbWrapperBounds.width) {
                this.navScroller.scrollLeft = this.selected.thumbnail.offsetLeft + thumbBounds.width - thumbWrapperBounds.width + 35;
            }
            else if (this.selected.thumbnail.offsetLeft < this.navScroller.scrollLeft) {
                this.navScroller.scrollLeft = this.selected.thumbnail.offsetLeft - 35;
            }
        }
    }
    _handleScrollButtonVisibility() {
        if (!this.navScrollerButtons[0]) {
            return;
        }
        if (Layout.isLessThanBreakpoint('S')) {
            // We use 4px here just to ensure the user has scrolled a little bit before
            // showing the buttons.
            if (this.navScroller.scrollLeft > 4) {
                this.navScrollerButtons[0].classList.add('visible');
            }
            else {
                this.navScrollerButtons[0].classList.remove('visible');
            }
            if (this.navScroller.scrollLeft < this.navScroller.scrollWidth - this.navScroller.clientWidth) {
                this.navScrollerButtons[1].classList.add('visible');
            }
            else {
                this.navScrollerButtons[1].classList.remove('visible');
            }
        }
        else {
            this.navScrollerButtons[0].classList.remove('visible');
            this.navScrollerButtons[1].classList.remove('visible');
        }
    }
    _onScrollButtonClick(scrollRight) {
        if (scrollRight) {
            this.navScroller.scrollLeft = this.navScroller.scrollLeft - 100;
        }
        else {
            this.navScroller.scrollLeft = this.navScroller.scrollLeft + 100;
        }
    }
    _closeImageZoom() {
        if (this.imageZoom) {
            this.imageZoom.unload();
            this.imageZoom = null;
        }
    }
    _openImageZoom() {
        if (this.selected.figure.dataset.mediaType !== 'image' ||
            (Layout.isGreaterThanBreakpoint('L') && this.settings.gallery_click_to_zoom === 'mobile') ||
            (Layout.isLessThanBreakpoint('L') && this.settings.gallery_click_to_zoom === 'desktop'))
            return;
        if (this.swiped) {
            this.swiped = false;
            return;
        }
        this._closeImageZoom();
        this.imageZoom = new ProductClickToZoom({
            settings: this.settings,
            slides: this.figures,
            originalThumbnails: this.thumbnails,
            startIndex: parseInt(this.selected.figure.dataset.galleryIndex, 10),
            selectGalleryIndex: (index) => this._selectMediaByIndex(index),
        });
    }
}
